import type {
  PlannableItem,
  PlannableOverlapExtended,
  Rest
} from '@libs/data-access/entities'
import { plannableService } from '@libs/data-access/queries'
import { map, keyBy, sumBy, filter } from 'lodash-es'
import moment from 'moment'
import { useEffect, useState } from 'react'

type Params = {
  accountId: number
  restType: string
  selectedRestType: string
  selectedContractsIds: string[]
  momentStart: moment.Moment
  momentEnd: moment.Moment
  rest: Rest
}
type UsePlannableOverlap = (args: Params) => PlannableOverlapExtended

export const usePlannableOverlap: UsePlannableOverlap = ({
  accountId,
  rest,
  restType,
  selectedRestType,
  momentStart,
  momentEnd,
  selectedContractsIds
}) => {
  const [overlapData, setOverlapData] = useState<PlannableOverlapExtended>({})

  const plannableStatsQuery = plannableService.stats.useFetchPlannableStats({
    queryParams: {
      starts_at: momentStart.format(),
      ends_at: momentEnd.format(),
      user_contract_ids: selectedContractsIds.map(Number)
    }
  })

  useEffect(() => {
    // Reinitialize plannable overlap data
    if (!selectedContractsIds.length) {
      return setOverlapData({})
    }

    // dont trigger fetch if rest is hour or type selected
    if (!restType) {
      return setOverlapData({})
    }

    const currentRestRange = moment.range(
      moment(rest.starts_at),
      moment(rest.ends_at)
    )
    const selectedRange = moment.range(momentStart, momentEnd)
    const shouldSubtractCurrentRest =
      rest.id &&
      selectedContractsIds[0] === String(rest.user_contract_id) &&
      currentRestRange.overlaps(selectedRange)

    if (momentEnd.isBefore(momentStart)) {
      return
    }
    const result = plannableStatsQuery.data?.result ?? []

    // if editing one rest and rest is still in the new date range
    // we subtract (-1) it from the total rest_count
    const isWeeklyRest = restType === 'weekly_rest'
    const isHourlyRest = rest.duration_type === 'hourly'
    const resultFiltered: PlannableItem[] =
      shouldSubtractCurrentRest && result?.[0]
        ? [
            {
              ...result[0],
              weekly_rest_count:
                result[0].weekly_rest_count - (isWeeklyRest ? 1 : 0),
              hourly_rest_count:
                result[0].hourly_rest_count - (isHourlyRest ? 1 : 0),
              other_rest_count:
                result[0].other_rest_count -
                (isWeeklyRest || isHourlyRest ? 0 : 1)
            }
          ]
        : result
    // we only return contracts with no rest on period before eventually deleting shifts
    const contractsWithoutRest = filter(resultFiltered, c => {
      const plannableIfWeeklyRest =
        (c.weekly_rest_count ?? 0) + (c.hourly_rest_count ?? 0)
      const plannableIfOtherRest =
        (c.other_rest_count ?? 0) + (c.hourly_rest_count ?? 0)
      const plannableIfHourlyRest =
        selectedRestType === 'hour' ? c.weekly_rest_count ?? 0 : 0
      const hasPlannableOverlap =
        restType === 'weekly_rest'
          ? plannableIfWeeklyRest === 0
          : plannableIfOtherRest + plannableIfHourlyRest === 0
      return hasPlannableOverlap
    })

    setOverlapData({
      result: resultFiltered,
      starts_at: momentStart.format(),
      ends_at: momentEnd.format(),
      byContractId: keyBy(resultFiltered, 'user_contract_id'),
      total_other_rest_count: sumBy(resultFiltered, 'other_rest_count'),
      total_weekly_rest_count: sumBy(resultFiltered, 'weekly_rest_count'),
      total_hourly_rest_count: sumBy(resultFiltered, 'hourly_rest_count'),
      contracts_rest_overlap: filter(resultFiltered, 'rest_count'),
      total_shift_count: sumBy(contractsWithoutRest, 'shift_count'),
      contracts_only_shift_overlap: filter(contractsWithoutRest, 'shift_count'),
      contract_ids_without_rest_overlap: map(contractsWithoutRest, i =>
        String(i.user_contract_id)
      )
    })

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    accountId,
    selectedRestType,
    restType,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    momentStart.format(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    momentEnd.format(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    selectedContractsIds.toString(),
    plannableStatsQuery.data
  ])

  return overlapData
}
